import {
  useEffect,
  useMemo,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { ApiError } from '@/lib/api';
import { Icon, I } from '@/components/icons';

export function PageHeader({
  title,
  subtitle,
  right,
}: {
  title: string;
  subtitle?: string;
  right?: ReactNode;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
        marginBottom: 20,
        flexWrap: 'wrap',
        gap: 12,
      }}
    >
      <div>
        <h1 style={{ margin: 0, fontSize: 20, color: 'var(--ink)' }}>{title}</h1>
        {subtitle && (
          <p style={{ margin: '4px 0 0', color: 'var(--ink-3)', fontSize: 13 }}>
            {subtitle}
          </p>
        )}
      </div>
      {right}
    </div>
  );
}

export function Pill({
  children,
  bg,
  color,
  style,
}: {
  children: ReactNode;
  bg: string;
  color: string;
  style?: CSSProperties;
}) {
  return (
    <span className="pill" style={{ background: bg, color, ...style }}>
      {children}
    </span>
  );
}

export const cellStyle: CSSProperties = {
  padding: '10px 14px',
  borderBottom: '1px solid var(--line-soft)',
  textAlign: 'left',
};

export const thStyle: CSSProperties = {
  ...cellStyle,
  color: 'var(--ink-3)',
  fontWeight: 500,
  fontSize: 12,
};

export const fieldStyle: CSSProperties = {
  padding: '7px 9px',
  border: '1px solid var(--line)',
  borderRadius: 'var(--r-md)',
  fontSize: 13,
  background: 'var(--surface)',
};

export interface Msg {
  kind: 'ok' | 'err';
  text: string;
}

export function useToast() {
  const [msg, setMsg] = useState<Msg | null>(null);
  const ok = (text: string) => setMsg({ kind: 'ok', text });
  const fail = (e: unknown) =>
    setMsg({ kind: 'err', text: e instanceof ApiError ? e.message : '操作失敗' });
  return { msg, ok, fail, clear: () => setMsg(null) };
}

export function Banner({ msg }: { msg: Msg | null }) {
  if (!msg) return null;
  return (
    <div
      style={{
        padding: '8px 12px',
        borderRadius: 'var(--r-md)',
        marginBottom: 16,
        background: msg.kind === 'ok' ? 'var(--success-bg)' : 'var(--danger-bg)',
        color: msg.kind === 'ok' ? 'var(--success)' : 'var(--danger)',
        fontSize: 13,
      }}
    >
      {msg.text}
    </div>
  );
}

export function Card({
  title,
  children,
  right,
  icon,
  pad = 18,
}: {
  title?: string;
  children: ReactNode;
  right?: ReactNode;
  icon?: ReactNode;
  pad?: number;
}) {
  return (
    <div className="card" style={{ padding: pad, marginBottom: 16 }}>
      {(title || right) && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 12,
            gap: 8,
          }}
        >
          <h3
            style={{
              margin: 0,
              fontSize: 14,
              color: 'var(--ink)',
              display: 'flex',
              alignItems: 'center',
              gap: 7,
            }}
          >
            {icon}
            {title}
          </h3>
          {right}
        </div>
      )}
      {children}
    </div>
  );
}

/* ---------- Tone-based Tag (prototype Pill) ---------- */
export type Tone =
  | 'default'
  | 'wallet'
  | 'ticket'
  | 'tag'
  | 'warn'
  | 'danger'
  | 'success'
  | 'primary'
  | 'ink'
  | 'line';

const TONES: Record<Tone, { bg: string; color: string }> = {
  default: { bg: 'var(--surface-3)', color: 'var(--ink-2)' },
  wallet: { bg: 'var(--wallet-bg)', color: 'var(--wallet)' },
  ticket: { bg: 'var(--ticket-bg)', color: 'var(--ticket)' },
  tag: { bg: 'var(--tag-bg)', color: 'var(--tag)' },
  warn: { bg: 'var(--warn-bg)', color: 'var(--warn)' },
  danger: { bg: 'var(--danger-bg)', color: 'var(--danger)' },
  success: { bg: 'var(--success-bg)', color: 'var(--success)' },
  primary: { bg: 'var(--primary-tint)', color: 'var(--primary-deep)' },
  ink: { bg: 'var(--ink)', color: '#fff' },
  line: { bg: '#06C75510', color: '#06C755' },
};

export function Tag({
  tone = 'default',
  children,
  dot = false,
  style,
}: {
  tone?: Tone;
  children: ReactNode;
  dot?: boolean;
  style?: CSSProperties;
}) {
  const t = TONES[tone];
  return (
    <span className="pill" style={{ background: t.bg, color: t.color, ...style }}>
      {dot && (
        <span
          style={{
            width: 5,
            height: 5,
            borderRadius: '50%',
            background: t.color,
            display: 'inline-block',
          }}
        />
      )}
      {children}
    </span>
  );
}

/* ---------- Avatar ---------- */
const AVATAR_PALETTE: Record<string, [string, string]> = {
  primary: ['#E8C7B8', '#8B5A48'],
  rose: ['#F4D5CC', '#A65A47'],
  sand: ['#EBDFCF', '#7A6650'],
  sage: ['#DBE5D6', '#5A7858'],
  blue: ['#D6E0EB', '#4A6985'],
  plum: ['#E5D5E0', '#7A5470'],
};

export function Avatar({
  name = '',
  size = 28,
  tone = 'auto',
}: {
  name?: string;
  size?: number;
  tone?: keyof typeof AVATAR_PALETTE | 'auto';
}) {
  const initials = useMemo(() => {
    if (!name) return '·';
    if (/[\u4e00-\u9fa5]/.test(name)) return name.length >= 2 ? name.slice(-2) : name;
    return name
      .split(' ')
      .map((w) => w[0])
      .slice(0, 2)
      .join('')
      .toUpperCase();
  }, [name]);
  const keys = Object.keys(AVATAR_PALETTE);
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h + name.charCodeAt(i)) % keys.length;
  const [bg, fg] =
    AVATAR_PALETTE[tone === 'auto' ? keys[h] : tone] ?? AVATAR_PALETTE.primary;
  return (
    <span
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: bg,
        color: fg,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: size * 0.42,
        fontWeight: 600,
        flexShrink: 0,
      }}
    >
      {initials}
    </span>
  );
}

/* ---------- Modal ---------- */
export function Modal({
  open,
  onClose,
  children,
  width = 560,
}: {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  width?: number;
}) {
  useEffect(() => {
    const h = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    if (open) window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 200,
        background: 'rgba(42, 33, 27, 0.32)',
        backdropFilter: 'blur(2px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width,
          maxWidth: '94vw',
          maxHeight: '92vh',
          overflow: 'auto',
          background: 'var(--surface)',
          borderRadius: 'var(--r-xl)',
          boxShadow: 'var(--shadow-xl)',
        }}
      >
        {children}
      </div>
    </div>
  );
}

export function ModalHeader({
  title,
  sub,
  onClose,
}: {
  title: string;
  sub?: string;
  onClose: () => void;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: 20,
        borderBottom: '1px solid var(--line)',
      }}
    >
      <div style={{ flex: 1 }}>
        <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>{title}</h2>
        {sub && (
          <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 2 }}>{sub}</div>
        )}
      </div>
      <button
        onClick={onClose}
        style={{ background: 'transparent', border: 0, color: 'var(--ink-3)', padding: 4 }}
      >
        <Icon d={I.close} size={18} />
      </button>
    </div>
  );
}

/* ---------- Form field ---------- */
export const inputStyle: CSSProperties = {
  width: '100%',
  padding: '7px 10px',
  border: '1px solid var(--line)',
  borderRadius: 6,
  background: 'var(--surface)',
  fontSize: 12,
  color: 'var(--ink)',
  outline: 'none',
};

export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <div
        style={{
          fontSize: 11,
          color: 'var(--ink-3)',
          fontWeight: 500,
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      {children}
    </div>
  );
}

/* ---------- Stat tile ---------- */
export function Stat({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: ReactNode;
  hint?: string;
  tone?: Tone;
}) {
  // 'ink' 的 pill 文字色是白色(配深底),但 Stat 數值放在白卡上 → 需用深色才看得見
  const color = !tone || tone === 'ink' ? 'var(--ink)' : TONES[tone].color;
  return (
    <div className="card" style={{ padding: 16, flex: '1 1 132px', minWidth: 0 }}>
      <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>{label}</div>
      <div
        className="num"
        style={{ fontSize: 24, fontWeight: 700, color, marginTop: 4 }}
      >
        {value}
      </div>
      {hint && (
        <div style={{ fontSize: 11, color: 'var(--ink-4)', marginTop: 2 }}>{hint}</div>
      )}
    </div>
  );
}

/* ---------- Empty state ---------- */
export function Empty({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        padding: '28px 0',
        textAlign: 'center',
        color: 'var(--ink-4)',
        fontSize: 13,
      }}
    >
      {children}
    </div>
  );
}
